import {
	Card,
	CardBody,
	PageLayout,
	Icon,
	Icons,
	useLangContext,
	CardHeader,
	StringHelpers,
	Variants,
	route,
	API_ENDPOINTS,
	Spinner,
	useDisabledContext,
	Badge,
	Pagination,
	DateFormats,
} from 'carrier-fe';
import { ReactNode, useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from 'date-fns';

const DEBOUNCE_TIME = 1000;
const PAGE_SIZE = 50;
const SEARCH_FILTER = 'trainingEventSearchFilter';
const SORT_BY = '-created_at';

interface GridProps<T> {
	endpoint: string;
	renderItem: (item: T, searchParams: URLSearchParams) => ReactNode;
	searchable?: boolean;
	pagination?: boolean;
}

interface Item {
	id: string;
	distributor_id?: string;
	name: string;
	description?: string;
	training_course_ids: string[];
	event_at: string;
	end_date: string;
	training_venue_id: string;
	attendee_count: number;
	joined?: boolean;
	availability_percentage: number;
	requires_payment: boolean;
	price_pence?: number;
	currency?: string;
	created_at: string;
	updated_at: string;
	deleted_at?: string;
	permissions: {
		view: boolean;
		update: boolean;
		delete: boolean;
		restore: boolean;
	};
	distributor: string;
	venue: string;
	course: string;
	training_venue?: {
		address?: {
			town_city?: string;
			state_county?: string;
		}
	}
}

interface ApiPaginatedListResponse<R> {
	data: {
		rows: R[];
		pagination: {
			pages: {
				current: number;
				total: number;
			};
			count: {
				current: number;
				total: number;
			};
		};
	};
}

function Grid<T extends Item>(props: GridProps<T>) {
	const { endpoint, renderItem, searchable, pagination } = props;
	const [searchParams, setSearchParams] = useSearchParams();
	const { crud } = useLangContext();

	const [items, setItems] = useState<T[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [search, setSearch] = useState<string>('');
	const [currentPage, setCurrentPage] = useState<number>(1);

	const { disabled } = useDisabledContext();

	const totalPages = useRef<number>(1);

	const fetch = () => {
		if (!loading) setLoading(true);

		axios
			.get<ApiPaginatedListResponse<T>>(
				endpoint + '?' + searchParams.toString()
			)
			.then((res) => {
				const { rows, pagination } = res.data.data;
				const { total, current } = pagination.pages;

				totalPages.current = total;
				setCurrentPage(current);
				setItems(rows);
			})
			.catch(console.error)
			.finally(() => setLoading(false));
	};

	function sortItems<T extends Item>(items: T[]): T[] {
		return items.sort((a, b) => {
			const dateComparison =
				new Date(a.event_at).getTime() - new Date(b.event_at).getTime();

			if (dateComparison !== 0) {
				return dateComparison;
			}

			return a.name.localeCompare(b.name);
		});
	}

	const renderGrid = () => {
		sortItems(items);

		if (items.length === 0) return <p>No records</p>;

		return (
			<div className="row row-cols-1 row-cols-sm-2 row-cols-xxl-3 gy-4">
				{items.map((item) => {
					return renderItem(item, searchParams);
				})}
			</div>
		);
	};

	useEffect(() => {
		const params: Record<string, string> = {};

		params['format'] = 'flat';
		params['page[size]'] = String(PAGE_SIZE);
		params['sort'] = SORT_BY;

		const savedFilter = sessionStorage.getItem(SEARCH_FILTER);

		if (savedFilter) setSearch(savedFilter);
		if (search && search.length > 0) params['filter[search]'] = search;
		if (currentPage) params['page[number]'] = String(currentPage);

		setSearchParams(params);
	}, [search, currentPage]);

	useEffect(() => {
		if (!endpoint) return;

		const debounce = setTimeout(() => fetch(), DEBOUNCE_TIME);
		return () => clearTimeout(debounce);
	}, [endpoint, searchParams.toString()]);

	return (
		<Card>
			<CardHeader
				leftSlot={
					searchable && (
						<div className={'d-flex flex-column align-items-start'}>
							<label
								htmlFor="search"
								className="visually-hidden"
							>
								{crud?.pages.index.search || 'Search'}
							</label>
							<div className="position-relative">
								<div className="position-absolute d-flex h-100 align-items-center ps-2 pe-none">
									<Icon
										icon={Icons.SEARCH}
										style={{ color: '#0d6efd' }}
									/>
								</div>
								<input
									type="search"
									id="search"
									name="search"
									value={search || ''}
									onChange={(e) => {
										sessionStorage.removeItem(SEARCH_FILTER);
										setSearch(e.target.value || '');
									}}
									className="form-control ps-5 py-2"
									placeholder={
										crud?.pages.index.search || 'Search'
									}
									disabled={disabled}
								/>
							</div>
						</div>
					)
				}
			/>
			<CardBody>
				{loading ? (
					<div className="d-flex justify-content-center">
						<Spinner />
					</div>
				) : (
					renderGrid()
				)}
				{pagination && (
					<div className="d-flex flex-row-reverse gap-3 mt-4">
						<Pagination
							currentPage={currentPage || 1}
							totalPages={totalPages.current || 1}
							onChange={(e) => setCurrentPage(e)}
							incrementButtons={true}
						/>
					</div>
				)}
			</CardBody>
		</Card>
	);
}

function TrainingEvents() {
	const navigate = useNavigate();
	const { fields } = useLangContext();
	const currency_formatter = useMemo(
		() =>
			new Intl.NumberFormat(undefined, {
				style: 'currency',
				currency: 'GBP',
			}),
		[]
	);

	const renderItem = (item: Item, searchParams: URLSearchParams) => {
		const price = !!item.price_pence
			? currency_formatter.format(item.price_pence / 100)
			: StringHelpers.title(fields?.free) || 'Free';

		const gotoEvent = () => {
			if (!item.permissions.view) return;
			const searchFilter = searchParams.get('filter[search]');
			if (searchFilter) {
				sessionStorage.setItem(SEARCH_FILTER, searchFilter);
			}
			navigate(item.id);
		};

		const getBadge = () => {
			switch (true) {
				case item.joined:
					return (
						<Badge
							label={StringHelpers.upper(
								fields?.joined || 'JOINED'
							)}
							variant={Variants.Success}
						/>
					);
				case item.availability_percentage === 100:
					return (
						<Badge
							label={StringHelpers.upper(
								fields?.available || 'Available'
							)}
							variant={Variants.Light}
						/>
					);
				case item.availability_percentage === 0:
					return (
						<Badge
							label={StringHelpers.upper(
								fields?.not_available || 'Not Available'
							)}
							variant={Variants.Dark}
						/>
					);
				default:
					return (
						<Badge
							label={StringHelpers.upper(
								`${fields?.availability || 'Availability'} ${
									item.availability_percentage
								}%`
							)}
							variant={Variants.Light}
						/>
					);
			}
		};

		const multiDayEvent = item.event_at !== item.end_date;

		return (
			<div
				className="col"
				onClick={gotoEvent}
				style={{ cursor: 'pointer' }}
			>
				<Card
					className={'h-100'}
					noMargin
				>
					<CardBody className="d-flex flex-column">
						<div className="mb-4">
							<h4 className="fw-bolder">
								{StringHelpers.title(item.name)}
							</h4>
							<div>{StringHelpers.title(item.course)}</div>
							<div>
								{multiDayEvent ?
									`${formatDate(item.event_at, DateFormats.DATE)} - ${formatDate(item.end_date, DateFormats.DATE)}`
									: formatDate(item.event_at, DateFormats.DATE)
								}
							</div>
							{(item.training_venue?.address?.town_city || item.training_venue?.address?.state_county) &&
								<div>
									{[item.training_venue?.address?.town_city, item.training_venue?.address?.state_county]
										.filter(Boolean)
										.join(', ')
									}
								</div>
							}
							{item.distributor && (
								<div>
									{StringHelpers.title(item.distributor)}
								</div>
							)}
						</div>
						<div className="d-flex align-items-center justify-content-between mt-auto">
							<span className="fw-bold fs-4">{price}</span>
							<div className="fw-light text-end">
								{getBadge()}
							</div>
						</div>
					</CardBody>
				</Card>
			</div>
		);
	};

	return (
		<PageLayout title={fields?.training_calendar || 'Training Calendar'}>
			<Grid<Item>
				endpoint={route(API_ENDPOINTS.TRAINING.EVENT.INDEX)}
				renderItem={renderItem}
				searchable
				pagination
			/>
		</PageLayout>
	);
}

export default TrainingEvents;
